<template>
  <BaseExtractedDetails 
    :sections="sections" 
    :has-data="hasData"
    :data="data"
  />
</template>

<script>
import BaseExtractedDetails from './BaseExtractedDetails.vue'

export default {
  name: 'VATStatementDetails',
  components: {
    BaseExtractedDetails
  },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    hasData() {
      return this.data && Object.keys(this.data).length > 0;
    },
    
    sections() {
      if (!this.data) return [];
      
      return [
        // Business Information
        {
          title: 'Business Information',
          icon: 'las la-building',
          type: 'info-grid',
          fields: [
            {
              key: 'business_info.name',
              label: 'Business Name',
              format: 'text'
            },
            {
              key: 'business_info.vat_number',
              label: 'VAT Number',
              format: 'text'
            },
            {
              key: 'business_info.tax_authority',
              label: 'Tax Authority',
              format: 'text'
            },
            {
              key: 'business_info.address',
              label: 'Address',
              format: 'text',
              fullWidth: true
            }
          ]
        },
        
        // Filing Period
        {
          title: 'Filing Period',
          icon: 'las la-calendar',
          type: 'info-grid',
          fields: [
            {
              key: 'business_info.filing_period.start_date',
              label: 'Start Date',
              format: 'date'
            },
            {
              key: 'business_info.filing_period.end_date',
              label: 'End Date',
              format: 'date'
            },
            {
              key: 'business_info.filing_date',
              label: 'Filing Date',
              format: 'date'
            },
            {
              key: 'business_info.country',
              label: 'Country',
              format: 'text'
            }
          ]
        },
        
        // Financial Summary
        {
          title: 'Financial Summary',
          icon: 'las la-chart-pie',
          type: 'summary-grid',
          cards: [
            {
              key: 'summary.total_sales',
              label: 'Total Sales',
              format: 'currency'
            },
            {
              key: 'summary.total_purchases',
              label: 'Total Purchases',
              format: 'currency'
            },
            {
              key: 'summary.total_vat_collected',
              label: 'VAT Collected',
              format: 'currency'
            },
            {
              key: 'summary.total_vat_paid',
              label: 'VAT Paid',
              format: 'currency'
            },
            {
              key: 'summary.net_vat_due',
              label: 'Net VAT Due',
              format: 'currency',
              highlight: true
            }
          ]
        },
        
        // VAT Breakdown
        ...(this.data.vat_breakdown && this.data.vat_breakdown.length > 0 ? [{
          title: 'VAT Breakdown',
          icon: 'las la-percentage',
          type: 'table',
          columns: [
            {
              key: 'rate',
              label: 'Rate',
              type: 'badge',
              format: 'rate',
              badgeClass: (value) => {
                if (value === 'reverse_charge') return 'rate-reverse';
                if (value === '0') return 'rate-zero';
                return 'rate-standard';
              }
            },
            {
              key: 'sales_amount',
              label: 'Sales Amount',
              format: 'currency'
            },
            {
              key: 'sales_vat',
              label: 'Sales VAT',
              format: 'currency'
            },
            {
              key: 'purchase_amount',
              label: 'Purchase Amount',
              format: 'currency'
            },
            {
              key: 'purchase_vat',
              label: 'Purchase VAT',
              format: 'currency'
            }
          ],
          data: this.data.vat_breakdown
        }] : []),
        
        // Adjustments
        ...(this.data.adjustments && this.data.adjustments.length > 0 ? [{
          title: 'Adjustments',
          icon: 'las la-adjust',
          type: 'list',
          fields: [
            {
              key: 'description',
              class: 'description'
            },
            {
              key: 'amount',
              class: 'amount',
              format: 'currency'
            },
            {
              key: 'type',
              class: 'type'
            }
          ],
          items: this.data.adjustments
        }] : []),
        
        // Declarations
        ...(this.data.declarations ? [{
          title: 'Declarations',
          icon: 'las la-signature',
          type: 'info-grid',
          fields: [
            {
              key: 'declarations.authorized_person_name',
              label: 'Authorized Person',
              format: 'text'
            },
            {
              key: 'declarations.designation',
              label: 'Designation',
              format: 'text'
            },
            {
              key: 'declarations.signature_date',
              label: 'Signature Date',
              format: 'date'
            }
          ]
        }] : []),
        
        // Notes
        ...(this.data.notes ? [{
          title: 'Notes',
          icon: 'las la-sticky-note',
          type: 'text',
          content: this.data.notes
        }] : [])
      ];
    }
  },
  
  methods: {
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : null;
      }, obj);
    }
  }
}
</script>
